import { useCallback, useMemo } from "react";

import { useSessionStorage } from "./useSessionStorage";

/** `clientId` tells same-named companies apart wherever the overridden company is named. */
export type CompanyOverride = { id: string; name: string; clientId?: string };

/**
 * The override is stored as `{ id, name }`. A session saved by an older build holds the string
 * `id,name` instead; an id never contains a comma, so everything after the first one is the name.
 */
export const parseCompanyOverride = (value: unknown): CompanyOverride | undefined => {
  if (typeof value === "string") {
    const separator = value.indexOf(",");
    return separator > 0
      ? { id: value.slice(0, separator), name: value.slice(separator + 1) }
      : undefined;
  }
  if (typeof value === "object" && value !== null) {
    const { id, name, clientId } = value as Partial<CompanyOverride>;
    if (typeof id === "string" && typeof name === "string") {
      return typeof clientId === "string" ? { id, name, clientId } : { id, name };
    }
  }
  return undefined;
};

export const useStorageCompanyOverride = () => {
  const [storageValue, setStorageValue, clear] = useSessionStorage<CompanyOverride | string>(
    "auth:roleManager:company",
  );

  const current = useMemo(() => parseCompanyOverride(storageValue), [storageValue]);
  const store = useCallback(
    (company: CompanyOverride) => setStorageValue(company),
    [setStorageValue],
  );

  return [current, store, clear] as const;
};
